import { useEffect, type ReactNode } from 'react'
import { rolesAtLeast, rolesSchema } from '@lms/shared-types'
import type { Role } from '@lms/shared-types'
import { useSession } from './auth-context'
import { BootSplash } from '../components/shell/BootSplash'

interface RequireRoleProps {
  minimum: Role
  children: ReactNode
  fallback?: ReactNode
  /**
   * When set, only these roles (plus exec_director, which always passes) may
   * access this route — even if a role satisfies `minimum` via the hierarchy.
   *
   * Use this to create HR-only or Director-only gates where the linear
   * hierarchy would otherwise let a higher-ranked parallel role through.
   *
   * Example: `<RequireRole minimum="hr" only={['hr', 'exec_director']}>` blocks
   * Director even though Director rank > HR rank.
   */
  only?: Role[]
}

function Forbidden(): JSX.Element {
  return (
    <div
      style={{
        padding: '48px 24px',
        textAlign: 'center',
        color: 'var(--ink-2)',
      }}
    >
      <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 28, fontWeight: 500 }}>
        403 — Forbidden
      </h1>
      <p>You don't have permission to view this page.</p>
    </div>
  )
}

/**
 * Bounce to the login page, preserving the current URL in `?next=…` so
 * we can deep-link back after a successful sign-in.
 *
 * Skipped during SSR / non-browser test environments. We use `useEffect`
 * (not a top-level navigate) so the redirect happens after the gate's
 * render returns — keeps render side-effect-free for React.
 */
function RedirectToLogin(): JSX.Element {
  useEffect(() => {
    if (typeof window === 'undefined') return
    const here = window.location.pathname + window.location.search
    // If we're already on /login (or on the login redirect path) don't
    // loop the redirect.
    if (here.startsWith('/login')) return
    const next = encodeURIComponent(here)
    // replace, not assign: the gated URL is carried in ?next=, so keeping it
    // as its own history entry only gives Back a page that bounces to login.
    window.location.replace(`/login?next=${next}`)
  }, [])

  return <div style={{ padding: 24 }}>Redirecting to sign in…</div>
}

export function RequireRole({
  minimum,
  children,
  fallback,
  only,
}: RequireRoleProps): JSX.Element {
  const { user, isLoading } = useSession()

  if (isLoading) {
    return <BootSplash />
  }

  if (!user) {
    // Not authenticated — bounce to login with the deep-link preserved.
    // Callers may still override via `fallback` (e.g. tests).
    if (fallback !== undefined) return <>{fallback}</>
    return <RedirectToLogin />
  }

  // Primary check: the actor's own role SET.
  // Fall back to [user.role] when roles is absent or empty (back-compat with
  // API responses that pre-date multi-role assignment).
  const rawRoles = user.roles && user.roles.length > 0 ? user.roles : [user.role]
  const parsedRoles = rolesSchema.safeParse(rawRoles)
  if (!parsedRoles.success) {
    return <>{fallback ?? <Forbidden />}</>
  }
  const roles = parsedRoles.data

  // When `only` is set, exec_director always passes (business apex), as do the
  // white-label system-apex roles admin / superadmin, but every other role
  // must appear in the allowlist.
  const rolePassesHierarchy = rolesAtLeast(roles, minimum)
  const rolePassesAllowlist =
    only === undefined ||
    roles.includes('exec_director') ||
    roles.includes('admin') ||
    roles.includes('superadmin') ||
    roles.some((r) => only.includes(r))

  if (rolePassesHierarchy && rolePassesAllowlist) {
    return <>{children}</>
  }

  // Authenticated but underprivileged — show 403, don't redirect.
  return <>{fallback ?? <Forbidden />}</>
}
